import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/nokchhonk", label: "Nok Chhonk" },
  { to: "/mozekaql", label: "Mozekaql" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-40 transition-colors duration-500 ${
        scrolled ? "glass-strong shadow-[0_10px_40px_rgba(0,0,0,0.45)]" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center">
          <img
            src="/tosiase-logo.png"
            alt="Tosiase Creatives"
            className="h-14 w-auto rounded-xl bg-white/95 p-1 shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
          />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `group relative font-mono text-[11px] uppercase tracking-[0.22em] transition-colors duration-300 ${
                  isActive ? "text-amber-400" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-gradient-to-r from-amber-400 to-cyan-400 transition-all duration-300 group-hover:w-full" />
            </NavLink>
          ))}
          <Link to="/contact" data-testid="nav-cta" className="btn btn-amber !px-6 !py-3 text-sm">
            Start a conversation
            <ArrowUpRight size={16} />
          </Link>
        </nav>

        <button
          type="button"
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="glass flex h-11 w-11 items-center justify-center rounded-full text-white lg:hidden"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="glass-strong fixed inset-x-0 top-20 z-30 flex flex-col gap-1 border-t border-white/10 px-6 pb-8 pt-4 lg:hidden"
          >
            {LINKS.map((l, i) => (
              <motion.div
                key={l.to}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.06 * i, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              >
                <NavLink
                  to={l.to}
                  data-testid={`nav-mobile-${l.to === "/" ? "home" : l.to.slice(1)}`}
                  className={({ isActive }) =>
                    `block border-b border-white/5 py-4 font-display text-2xl font-medium ${
                      isActive ? "text-amber-400" : "text-slate-200"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </motion.div>
            ))}
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
